import React, { Component } from 'react';
import {
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@material-ui/core';
import SelectReact from 'react-select';
import PropTypes from 'prop-types';
import i18n from '../../../i18n';

import { isUniqueString } from '../../../helpers/helpers';
import {
  determineLinkedLocation,
  findMappedFromLocationId,
  groupMappedLocationOptions,
  shouldBeFed,
  shouldBeMapped,
} from '../../../helpers/locationHelpers';
import { customMappedLocationStyles } from '../../../assets/stylesheets/ReactSelectStyles';
import '../../../assets/stylesheets/Setting.css';

const baseState = {
  isActive: true,
  locationName: '',
  locationTypeId: 0,
  mappedLocationOptions: [],
  selectedLocationType: null,
  selectedMappedLocation: null,
  errors: {
    nameError: true,
    locationTypeError: true,
  }
};

class LocationUpdateModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...baseState,
    };
  }

  componentDidMount = () => {
    const { allLocations, location } = this.props;
    if (location) {
      this.refreshState();
    } else {
      const allLocationNames = allLocations.map(
        locationEntity => locationEntity.locationName
      );
      this.setState({
        allLocationNames,
        ...baseState,
      });
    }
  }

  clearState = () => {
    const {
      allLocations
    } = this.props;

    const allLocationNames = allLocations.map(
      locationEntity => locationEntity.locationName
    );

    this.setState({
      allLocationNames,
      ...baseState,
    });
  }

  handleMappedLocationChange = event => {
    this.setState({
      selectedMappedLocation: event,
    });
  };

  handleCheckbox = () => {
    this.setState(prevState => ({
      isActive: !prevState.isActive
    }));
  }

  handleLocationTypeChange = event => {
    const {
      allLocations
    } = this.props;
    const locationTypeId = event ? event.id : 0;
    const mappedLocationOptions = groupMappedLocationOptions(allLocations, {locationTypeId});
    this.setState(prevState => (
      {
        // eslint-disable-next-line react/no-unused-state
        locationTypeId,
        mappedLocationOptions,
        selectedLocationType: event,
        errors: {
          ...prevState.errors,
          locationTypeError: locationTypeId === 0,
        }
      }
    ));
  }

  handleNameChange = event => {
    const { allLocationNames } = this.state;
    const {
      target: { name, value }
    } = event;
    this.setState(prevState => ({
      [name]: value,
      errors: {
        ...prevState.errors,
        nameError: !isUniqueString(value.trim(), allLocationNames),
      } 
    }));
  }

  handleAdd = () => {
    const { addLocation } = this.props;
    const {
      isActive,
      locationName,
      locationTypeId,
      selectedMappedLocation
    } = this.state;

    const params = {
      isActive,
      locationName: locationName.toUpperCase(),
      locationTypeId,
      mappedToLocationId: selectedMappedLocation !== null && shouldBeMapped(locationTypeId)
        ? selectedMappedLocation.id
        : undefined,
      mappedFromLocationId: selectedMappedLocation !== null && shouldBeFed(locationTypeId)
        ? selectedMappedLocation.id
        : undefined
    }
    addLocation(params);
  }

  handleEdit = () => {
    const { allLocations, updateLocation, location } = this.props;
    const {
      isActive,
      locationName,
      selectedMappedLocation,
    } = this.state;
    
    const selectedMappedLocationValue = selectedMappedLocation ? selectedMappedLocation.id : null;
    const params = {
      locationId: location.id,
      isActive: isActive === location.isActive ? undefined : isActive,
      locationName: locationName === location.locationName ? undefined : locationName.trim(),
      mappedToLocationId: shouldBeMapped(location.locationTypeId) && location.mappedToLocationId !== selectedMappedLocationValue
        ? selectedMappedLocationValue
        : undefined,
      mappedFromLocationId: shouldBeFed(location.locationTypeId) && 
        findMappedFromLocationId(location, allLocations) !== selectedMappedLocationValue
          ? selectedMappedLocationValue
          : undefined,
    };
    updateLocation(params);
  };

  refreshState = () => {
    const {
      allLocations,
      location
    } = this.props;

    const {
      isActive,
      locationName,
    } = location;

    const allLocationNames = allLocations.map(
      locationEntity => locationEntity.locationName
    ).filter(name => name !== locationName);

    const mappedLocationOptions = groupMappedLocationOptions(allLocations, location);

    const selectedMappedLocation = shouldBeFed(location.locationTypeId) || shouldBeMapped(location.locationTypeId)
      ? determineLinkedLocation(location, mappedLocationOptions)
      : null;

    this.setState({
      allLocationNames,
      isActive,
      locationName,
      mappedLocationOptions,
      selectedMappedLocation,
      errors: {
        nameError: false,
      },
    });
  }

  render() {
    const { showEditModal, hideEditModal, location, locationTypeOptions } = this.props;
    const {
      locationName,
      mappedLocationOptions,
      selectedLocationType,
      selectedMappedLocation,
      isActive,
      errors,
    } = this.state;

    const title = location
      ? i18n.t('setting:editModalType', { type: location.locationType.locationTypeName})
      : i18n.t('setting:addLocation');

    const  { locationTypeId } = location || this.state;

    const shouldDisplayMapped = shouldBeMapped(locationTypeId);
    const shouldDisplayFed = shouldBeFed(locationTypeId);

    return (
      <Modal open={showEditModal} onClose={hideEditModal}>
        <Paper className="editModal">
          <Grid container>
            <Grid item xs={12}>
              <span>{title}</span>
            </Grid>
            <Grid item xs={12}>
              <Table>
                <TableBody>
                  {!location &&
                  (
                    <TableRow>
                      <TableCell className="attributeLabel">
                        {i18n.t('location:locationType')}
                      </TableCell>
                      <TableCell>
                        <SelectReact 
                          fullWidth
                          value={selectedLocationType}
                          hideSelectedOptions
                          className="settings-select-filed"
                          options={locationTypeOptions}
                          onChange={this.handleLocationTypeChange}
                        />
                      </TableCell>
                    </TableRow>
                  )
                  }
                  <TableRow>
                    <TableCell className="attributeLabel">
                      {i18n.t('location:isActive')}
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isActive}
                              color="secondary"
                              name="isActive"
                              onChange={this.handleCheckbox}
                            />
                          }
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="attributeLabel">
                      {i18n.t('location:locationName')}
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        onChange={this.handleNameChange}
                        name="locationName"
                        value={locationName}
                        error={errors.nameError}
                      />
                    </TableCell>
                  </TableRow>
                  {(shouldDisplayMapped || shouldDisplayFed) &&
                    (
                      <TableRow>
                        <Tooltip title={
                          <h2>
                            {shouldDisplayMapped
                            ? i18n.t('location:mappedToLocationHelp')
                            : i18n.t('location:mappedFromLocationHelp')
                            }
                          </h2>}
                        >
                          <TableCell className="attributeLabel">
                            {shouldDisplayMapped
                            ? i18n.t('location:mappedToLocation')
                            : i18n.t('location:mappedFromLocation')
                            }
                          </TableCell>
                        </Tooltip>
                        <TableCell>
                          <SelectReact
                            fullWidth
                            isClearable
                            value={selectedMappedLocation}
                            styles={customMappedLocationStyles}
                            placeholder={i18n.t('none')}
                            hideSelectedOptions
                            className="settings-select-filed"
                            options={mappedLocationOptions}
                            onChange={this.handleMappedLocationChange}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  }
                </TableBody>
              </Table>
            </Grid>
            <Grid item xs={12}>
              <br />
              <Button
                onClick={location ? this.handleEdit : this.handleAdd}
                disabled={Object.values(errors).includes(true)}
              >
                {i18n.t('confirm')}
              </Button>
              <Button onClick={hideEditModal}>{i18n.t('cancel')}</Button>
            </Grid>
          </Grid>
        </Paper>
      </Modal>
    )
  }
}

LocationUpdateModal.propTypes = {
  addLocation: PropTypes.func.isRequired,
  allLocations: PropTypes.arrayOf(PropTypes.object).isRequired,
  showEditModal: PropTypes.bool.isRequired,
  hideEditModal: PropTypes.func.isRequired,
  updateLocation: PropTypes.func.isRequired,
  location: PropTypes.shape({
    id: PropTypes.number,
    locationName: PropTypes.string,
    locationType: PropTypes.shape({
      locationTypeName: PropTypes.string,
    }),
    locationTypeId: PropTypes.number,
    slackChannelWebhook: PropTypes.string,
    isActive: PropTypes.bool,
    mappedToLocationId: PropTypes.number,
  }),
  locationTypeOptions: PropTypes.arrayOf(PropTypes.object),
};

LocationUpdateModal.defaultProps = {
  locationTypeOptions: [],
  location: null,
};

export default LocationUpdateModal;
